import React from "react";
//#region types
import { CommonEnums, StyleEnums } from "models/enums";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { MessageOptions } from "models/propTypes/messagePropTypes";
//#endregion
//#region components
import { useFloatingMessages, FloatingMessageWithLink } from "components/common";
import { useFeatureService, useIncomingInvoiceService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import { FeatureConstants } from "utils/constants";

const useCreateSuccessMessages = () => {
  const incomingInvoiceService = useIncomingInvoiceService();
  const featureService = useFeatureService();
  const { enqueueMessage } = useFloatingMessages();

  const [isRouteGenericVoucherToSupplierInvoiceEnabled, setIsRouteGenericVoucherToSupplierInvoiceEnabled] = React.useState<boolean>(false);

  React.useEffect(() => {
    featureService.hasFeature(FeatureConstants.RouteGenericVoucherToSupplierInvoice).then((res) => {
      setIsRouteGenericVoucherToSupplierInvoiceEnabled(res);
    });
  }, []);

  function enqueueMessageByDocumentType(type?: CommonEnums.DocumentTypes) {
    const defaultOptions: MessageOptions = {
      autoHideDuration: 60000,
      preventDuplicate: false,
      addToDismissList: true,
    };

    switch (type) {
      case CommonEnums.DocumentTypes.SelfInvoice:
        enqueueMessage(
          LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.SelfInvoiceCreateSuccess),
          StyleEnums.DisplayType.Success,
          true,
          false,
          defaultOptions
        );
        break;

      case CommonEnums.DocumentTypes.IncomingInvoice:
        enqueueMessage(
          <FloatingMessageWithLink
            message={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Document.IncomingInvoiceCreateSuccess,
              "",
              {
                status: LocalizationHelperInstance.getTranslatedEnumValue(
                  "IncomingInvoice.ViewState",
                  incomingInvoiceService.toViewState(WintSuperkollAPIModel.IncomingInvoiceState.Unhandled)
                ),
              }
            )}
            link={UrlHelper.formatPath(
              `/Reports/Expenses/IncomingInvoices?state=${WintSuperkollAPIModel.IncomingInvoiceState.Unhandled}`
            )}
          />,
          StyleEnums.DisplayType.Success,
          true,
          true,
          defaultOptions
        );
        break;

      case CommonEnums.DocumentTypes.Receipt:
        enqueueMessage(
          LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Document.ReceiptCreateSuccess),
          StyleEnums.DisplayType.Success,
          true,
          false,
          defaultOptions
        );
        break;

      case CommonEnums.DocumentTypes.GenericDocument:
        enqueueMessage(
          <FloatingMessageWithLink
              message={LocalizationHelperInstance.getFormattedMessage(
                isRouteGenericVoucherToSupplierInvoiceEnabled
                  ? TranslationKeys.Document.GenericDocumentCreateSuccess
                  : TranslationKeys.Document.GenericDocumentCreateSuccessFeatureFlagOff,
                "",
              {
                //"Here's a long message that will be split into multiple lines \n if it exceeds the maximum length."
                status: LocalizationHelperInstance.getTranslatedEnumValue(
                  "Document.GenericDocumentState",
                  WintSuperkollAPIModel.GenericDocumentState.SentIn
                ),
              }
            )}
            link={UrlHelper.formatPath(
              isRouteGenericVoucherToSupplierInvoiceEnabled
                ? `/Reports/DocumentDrafts`
                : `/Reports/AccountingDocuments?states=${WintSuperkollAPIModel.GenericDocumentState.SentIn}`
            )}
          />,
          StyleEnums.DisplayType.Success,
          true,
          true,
          { ...defaultOptions, autoHideDuration: 8000 }
        );
        break;

      default:
        enqueueMessage(
          LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Document.CreateSuccess),
          StyleEnums.DisplayType.Success,
          true,
          false,
          defaultOptions
        );
        break;
    }
  }

  return { enqueueMessageByDocumentType };
};

export default useCreateSuccessMessages;
